/**
 * @file Frontend utilities — a2FuelEmbodiedEnergy.ts.
 */

import type { FormDataResponse } from "../interfaces/formInterfaces";
import {
  classifyFuelMixForA1EmbodiedEnergy,
  computeA1EmbodiedEnergyMjFromFuelRow,
  resolveA1EmbodiedEnergyMjUnitSelectValue,
  resolveA1UnitLabelFromValue,
  type A1FuelRowCalcInputs,
} from "./a1FuelEmbodiedEnergy";

const A2_FUEL_CODE = "A2_FUEL_TRANSPORT";
const FUEL_MIX_FIELD = "fuelMix_01";

function parseNum(raw: string | undefined): number {
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

function getA2FuelMixLabel(formData: FormDataResponse, mixValue: string): string {
  const fuelSec = formData.formSubSections?.find((s) => s.code === A2_FUEL_CODE);
  const field = fuelSec?.fields?.find((f) => f.name === FUEL_MIX_FIELD);
  const opts = field?.dropdown?.options || field?.dropdownValues || [];
  const hit = opts.find(
    (o: { value?: string | number; label?: string }) =>
      String(o.value) === String(mixValue)
  );
  return String(hit?.label ?? hit?.value ?? "");
}

function getFieldDropdownOptions(
  formData: FormDataResponse,
  sectionCode: string,
  fieldName: string
): { value?: string | number; label?: string }[] {
  const sec = formData.formSubSections?.find((s) => s.code === sectionCode);
  const field = sec?.fields?.find((f) => f.name === fieldName);
  return field?.dropdown?.options || field?.dropdownValues || [];
}

/** Type definition — A2Embodied Energy Build Options. */
export type A2EmbodiedEnergyBuildOptions = {
  /** Numeric MJ unit id when units index is loaded; else `"MJ"`. */
  mjUnitId?: string | null;
  /** Flat fuel row keys `row0` … — skip auto overwrite when user edited embodied energy. */
  manualByFlatFuelRow?: Record<string, boolean>;
  /** Numeric unit id → token (kWh, L, kg, MT, …) */
  unitTokenById?: Record<string, string>;
};

/**
 * Auto-fill `embodiedEnergy_*` / `embodiedEnergy_unit_*` on A2 fuel rows (MJ).
 */
export function buildA2EmbodiedEnergyValueUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  sectionRows: Record<string, number>,
  options?: A2EmbodiedEnergyBuildOptions
): Record<string, string> {
  if (!formData) return {};

  const fuelSec = formData.formSubSections?.find((s) => s.code === A2_FUEL_CODE);
  if (!fuelSec?.code) return {};

  const fuelCode = fuelSec.code;
  const rows = Math.max(0, sectionRows[fuelCode] ?? 0);
  const updates: Record<string, string> = {};
  const mjUnit = options?.mjUnitId?.trim() || "MJ";
  const manual = options?.manualByFlatFuelRow ?? {};
  const unitTokenById = options?.unitTokenById ?? {};

  const qtyUnitOpts = getFieldDropdownOptions(
    formData,
    fuelCode,
    "fuelQuantity_unit"
  );
  const densityUnitOpts = getFieldDropdownOptions(
    formData,
    fuelCode,
    "fuelDensity_unit"
  );

  for (let i = 0; i < rows; i++) {
    const rowKey = `row${i}`;
    if (manual[rowKey]) continue;

    const mix = String(
      values[`${FUEL_MIX_FIELD}_${fuelCode}_row${i}`] ?? ""
    ).trim();
    const qty = parseNum(values[`fuelQuantity_${fuelCode}_row${i}`]);
    const cal = parseNum(values[`calorificValue_${fuelCode}_row${i}`]);
    const qtyUnitRaw = values[`fuelQuantity_unit_${fuelCode}_row${i}`] ?? "";
    const density = parseNum(values[`fuelDensity_${fuelCode}_row${i}`]);
    const densityUnitRaw = values[`fuelDensity_unit_${fuelCode}_row${i}`] ?? "";

    const eKey = `embodiedEnergy_${fuelCode}_row${i}`;
    const uKey = `embodiedEnergy_unit_${fuelCode}_row${i}`;

    if (!mix) {
      const existing = String(values[eKey] ?? "").trim();
      if (existing) continue;
      updates[eKey] = "";
      updates[uKey] = "";
      continue;
    }

    const label = getA2FuelMixLabel(formData, mix);
    const cat = classifyFuelMixForA1EmbodiedEnergy(mix, label);
    const extras: A1FuelRowCalcInputs = {
      quantityUnitValue: qtyUnitRaw,
      quantityUnitLabel: resolveA1UnitLabelFromValue(
        qtyUnitRaw,
        qtyUnitOpts,
        unitTokenById
      ),
      fuelDensity: Number.isFinite(density) ? density : undefined,
      densityUnitLabel: resolveA1UnitLabelFromValue(
        densityUnitRaw,
        densityUnitOpts,
        unitTokenById
      ),
    };
    const mj = computeA1EmbodiedEnergyMjFromFuelRow(cat, qty, cal, extras);

    if (mj == null) {
      const existing = String(values[eKey] ?? "").trim();
      if (existing) continue;
      updates[eKey] = "";
      updates[uKey] = mjUnit;
    } else {
      updates[eKey] = String(mj);
      updates[uKey] = mjUnit;
    }
  }

  return updates;
}

/** Default / upgrade embodied-energy unit tokens to MJ unit id. */
export function buildDefaultA2EmbodiedEnergyUnitUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  sectionRows: Record<string, number>,
  options?: A2EmbodiedEnergyBuildOptions
): Record<string, string> {
  if (!formData) return {};

  const fuelSec = formData.formSubSections?.find((s) => s.code === A2_FUEL_CODE);
  if (!fuelSec?.code) return {};

  const fuelCode = fuelSec.code;
  const rows = Math.max(0, sectionRows[fuelCode] ?? 0);
  const updates: Record<string, string> = {};
  const mjUnit = options?.mjUnitId?.trim() || "MJ";

  for (let i = 0; i < rows; i++) {
    const uKey = `embodiedEnergy_unit_${fuelCode}_row${i}`;
    const v = String(values[uKey] ?? "").trim();
    if (/^[0-9]+$/.test(v)) continue;
    if (v === "kWh") continue;
    if (v === "" || v === "MJ") {
      if (v !== mjUnit) updates[uKey] = mjUnit;
    }
  }

  return updates;
}

export { resolveA1EmbodiedEnergyMjUnitSelectValue as resolveA2EmbodiedEnergyMjUnitSelectValue };
